import React, { useEffect, useState } from "react";
import { X, Upload, FileText, Type, AlignLeft, AlertCircle } from "lucide-react";
import { createSurvey } from "../Services/SurveyService";
import RichTextEditor from "../../../Utility/RichTextEditor"; // adjust the relative path to match your actual folder depth
import sidebarImage from "../../../assests/SidebarImage.png";
import SidebarHeaderImage from "../../../assests/SidebarHeaderImage.png";

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// TODO: duplicated from AddQuestions.jsx — pull into a shared utils module
// once RichTextEditor is used in a third place.
const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent || "").trim();
};

const CreateSurveyModal = ({ isOpen, onClose, onSuccess }) => {
  const [surveyName, setSurveyName] = useState("");
  const [description, setDescription] = useState("");
  const [editorResetKey, setEditorResetKey] = useState(0);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({ surveyName: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setErrors({ surveyName: "", description: "" });
    }
  }, [isOpen]);

  const handleCreateSurvey = async () => {
    const plainDescription = stripHtml(description);

    const nextErrors = {
      surveyName: surveyName.trim() ? "" : "Assessment name is required",
      description: plainDescription ? "" : "Assessment description is required",
    };

    if (nextErrors.surveyName || nextErrors.description) {
      setErrors(nextErrors);
      return;
    }

    try {
      setSubmitting(true);
      const response = await createSurvey(surveyName, description, file);
      if (response.data.status.toLowerCase() === "success") {
        CustomToast("success", response.data.message);

        setSurveyName("");
        setDescription("");
        setEditorResetKey((key) => key + 1); // forces RichTextEditor to remount with empty content
        setFile(null);
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        CustomToast("fail", response.data.message);
      }
    } catch (error) {
      CustomToast("fail", error.response?.data?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-[2px]
        transition-opacity duration-300 z-[9998]
        ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[790px]
        bg-white shadow-2xl z-[9999]
        rounded-l-2xl overflow-hidden 
        transform transition-transform duration-300 ease-in-out
        flex flex-col
        ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="relative overflow-hidden flex items-center justify-between px-6 py-5 border-b bg-green-50">
          <div
            className="absolute inset-0 bg-no-repeat bg-cover bg-center pointer-events-none"
            style={{ backgroundImage: `url(${SidebarHeaderImage})` }}
          />
          <div className="absolute inset-0 bg-black/35 pointer-events-none" />
          <div className="relative z-10 flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div className="bg-[#7f35b2]/30 p-2.5 rounded-xl shrink-0">
                <FileText className="text-[#ffffff]" size={22} />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-100">Create Assessment</h2>
                <p className="text-sm text-gray-100 mt-0.5">Add a new Assessment</p>
              </div>
            </div>

            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-200/60 transition text-gray-100">
              <X size={22} />
            </button>
          </div>
        </div>

        <div className="relative flex-1 overflow-hidden">
          <div
            className="absolute inset-0 bg-no-repeat bg-right-bottom opacity-[0.07] pointer-events-none"
            style={{ backgroundImage: `url(${sidebarImage})`, backgroundSize: "360px auto" }}
          />

          <div className="relative h-full overflow-y-auto p-6">
            <div className="space-y-6">
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                  <Type size={14} className="text-[#7f35b2]" />
                  Assessment Name
                  <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  value={surveyName}
                  onChange={(e) => {
                    setSurveyName(e.target.value);
                    if (errors.surveyName) setErrors((prev) => ({ ...prev, surveyName: "" }));
                  }}
                  placeholder="Enter Assessment name"
                  className={`w-full border rounded-xl px-4 py-3 focus:ring-2 focus:border-transparent outline-none transition ${errors.surveyName
                    ? "border-red-300 focus:ring-red-200"
                    : "border-gray-300 focus:ring-[#7f35b2]"
                    }`}
                />

                {errors.surveyName && (
                  <p className="flex items-center gap-1 text-xs text-red-600 mt-1.5">
                    <AlertCircle size={12} />
                    {errors.surveyName}
                  </p>
                )}
              </div>

              {/* Description — rich text, no table insertion */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                  <AlignLeft size={14} className="text-[#7f35b2]" />
                  Description
                  <span className="text-red-500">*</span>
                </label>

                <div className={errors.description ? "rounded-xl ring-2 ring-red-200" : ""}>
                  <RichTextEditor
                    key={editorResetKey}
                    initialContent={description}
                    placeholder="Enter Assessment description"
                    minHeight={140}
                    enableTable={false}
                    onChange={(html) => {
                      setDescription(html);
                      if (errors.description) setErrors((prev) => ({ ...prev, description: "" }));
                    }}
                  />
                </div>

                {errors.description && (
                  <p className="flex items-center gap-1 text-xs text-red-600 mt-1.5">
                    <AlertCircle size={12} />
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Upload Section — unchanged */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                  <Upload size={14} className="text-[#7f35b2]" />
                  Upload File
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>

                <label className="border-2 border-dashed border-gray-300 rounded-2xl p-10 flex flex-col items-center justify-center cursor-pointer hover:border-[#7f35b2] hover:bg-[#7f35b2]/5 transition">
                  <Upload className="text-[#7f35b2] mb-4" size={40} />
                  <p className="font-semibold text-gray-800">Click to Upload</p>
                  <p className="text-sm text-gray-500 mt-1">PDF, DOCX, XLSX</p>
                  <input type="file" hidden onChange={(e) => setFile(e.target.files[0] ?? null)} />
                </label>

                {file && (
                  <div className="mt-4 flex items-center justify-between gap-3 bg-green-50 border border-green-200 rounded-xl p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={18} className="text-green-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-700 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setFile(null)}
                      className="p-1.5 rounded-lg hover:bg-green-100 text-gray-500 shrink-0"
                      title="Remove file"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer — unchanged */}
        <div className="border-t bg-white px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-5 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateSurvey}
            disabled={submitting}
            className="px-5 py-2.5 bg-[#7f35b2] hover:bg-[#3bb290] text-white rounded-xl transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Creating..." : "Create Assessment"}
          </button>
        </div>
      </div>
    </>
  );
};

export default CreateSurveyModal;
